import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { Logo } from '@/components/layout/Logo';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { ThemeToggle } from '@/components/layout/ThemeToggle';

interface PublicAuthLayoutProps {
  title: string;
  description?: string;
  children: ReactNode;
}

/**
 * Coque des pages d'authentification accessibles sans session
 * (mot de passe oublie, definition d'un nouveau mot de passe).
 * Shell for the authentication pages reachable without a session.
 */
export function PublicAuthLayout({ title, description, children }: PublicAuthLayoutProps) {
  const { t } = useLanguage();

  return (
    <div className="flex min-h-screen flex-col bg-background px-5 py-8 sm:px-8">
      <header className="mx-auto flex w-full max-w-md items-center justify-between">
        <Link to="/login" className="flex items-center gap-2.5">
          <Logo size={34} />
          <span className="font-display text-lg font-bold text-ink">{t('app.name')}</span>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSwitcher compact />
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-10">
        <div className="mb-7 space-y-2">
          <h1 className="font-display text-2xl font-bold text-ink">{title}</h1>
          {description ? <p className="text-sm text-ink-muted">{description}</p> : null}
        </div>

        {children}

        <p className="mt-8 flex flex-wrap items-center justify-between gap-3 text-xs text-ink-muted">
          <Link to="/login" className="inline-flex items-center gap-1.5 font-medium text-primary underline underline-offset-2">
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            {t('auth.backToSignIn')}
          </Link>
          <Link to="/help" className="font-medium text-primary underline underline-offset-2">
            {t('help.title')}
          </Link>
        </p>
      </main>
    </div>
  );
}
